import { type IAppointment } from '@principle-theorem/principle-core/interfaces';
import {
  isResolved,
  type DocumentReference,
  type Unresolved,
  type UnresolvedOr,
} from '@principle-theorem/shared';

export type AppointmentRefInput = UnresolvedOr<
  DocumentReference<IAppointment>
> | null;

export type AutoApplyEnabledInput = boolean | Unresolved | null;

export enum AutoApplyEligibility {
  Eligible = 'eligible',
  Unresolved = 'unresolved',
  Excluded = 'excluded',
}

/**
 * Centralises the appointment-credit input decisions so payment gating and
 * automatic capture always apply the same resolved, excluded, and pending rules.
 */
export class AppointmentCreditInputs {
  /**
   * Narrows an async appointment input before downstream payment paths use it
   * as a document reference.
   */
  static isResolvedAppointmentRef(
    appointmentRef: AppointmentRefInput
  ): appointmentRef is DocumentReference<IAppointment> {
    return isResolved(appointmentRef);
  }

  /**
   * Resolves one canonical capture target so automatic application cannot run
   * until both the appointment and its enablement setting have settled.
   */
  static resolveAutoApplyTarget(
    appointmentRef: AppointmentRefInput,
    autoApplyEnabled: AutoApplyEnabledInput
  ): DocumentReference<IAppointment> | undefined {
    return AppointmentCreditInputs.isResolvedAppointmentRef(appointmentRef) &&
      autoApplyEnabled === true
      ? appointmentRef
      : undefined;
  }

  /**
   * Derives the payment gate from the same target decision so automatic capture
   * and manual-payment readiness cannot disagree while inputs are settling.
   */
  static resolveAutoApplyEligibility(
    appointmentRef: AppointmentRefInput,
    autoApplyEnabled: AutoApplyEnabledInput
  ): AutoApplyEligibility {
    if (
      AppointmentCreditInputs.resolveAutoApplyTarget(
        appointmentRef,
        autoApplyEnabled
      )
    ) {
      return AutoApplyEligibility.Eligible;
    }
    return appointmentRef === undefined || autoApplyEnabled === false
      ? AutoApplyEligibility.Excluded
      : AutoApplyEligibility.Unresolved;
  }
}
